"use client"

import dynamic from "next/dynamic"
import Image from "next/image"
import Link from "next/link"
import {
  ArrowDown,
  ArrowUp,
  Clock3,
  Flame,
  Gauge,
  Heart,
  RotateCcw,
  Shield,
  Sparkles,
  Swords,
  Trophy,
  Zap,
} from "lucide-react"
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { trackProductEvent } from "@/lib/analytics/client"
import {
  playCorrectSound,
  playGameOverSound,
  playSelectSound,
  playUrgentTickSound,
  playVictorySound,
  playWrongSound,
} from "@/lib/sounds"
import { formatValue, shuffleArray } from "@/lib/utils"
import type {
  MonsterHuntDeck,
  MonsterHuntDifficulty,
  MonsterHuntPhase,
  MonsterImpact,
} from "@/components/monster-hunt/types"
import styles from "./monster-hunt.module.css"

const MonsterScene = dynamic(
  () =>
    import("@/components/monster-hunt/monster-scene").then(
      (module) => module.MonsterScene,
    ),
  {
    ssr: false,
    loading: () => (
      <div className={styles.sceneLoading}>
        <span />
        <strong>Building the arena</strong>
      </div>
    ),
  },
)

const DIFFICULTIES = {
  explorer: {
    id: "explorer",
    label: "Explorer",
    level: "Easy",
    description: "More time, stronger hits, and a 1.3-second guard window.",
    bossHealth: 160,
    playerDamage: 28,
    monsterDamage: 14,
    seconds: 18,
    guardWindowMs: 1300,
    guardedDamageFactor: 0.3,
    icon: Shield,
  },
  hunter: {
    id: "hunter",
    label: "Hunter",
    level: "Normal",
    description: "Balanced damage with a focused 0.95-second guard window.",
    bossHealth: 210,
    playerDamage: 22,
    monsterDamage: 22,
    seconds: 12,
    guardWindowMs: 950,
    guardedDamageFactor: 0.4,
    icon: Swords,
  },
  titan: {
    id: "titan",
    label: "Titan",
    level: "Hard",
    description: "Light attacks, brutal counters, and a 0.68-second guard.",
    bossHealth: 260,
    playerDamage: 18,
    monsterDamage: 30,
    seconds: 8,
    guardWindowMs: 680,
    guardedDamageFactor: 0.55,
    icon: Flame,
  },
} as const

const difficultyOrder: MonsterHuntDifficulty[] = [
  "explorer",
  "hunter",
  "titan",
]

interface BattleRound {
  id: string
  deck: MonsterHuntDeck
  current: MonsterHuntDeck["items"][number]
  challenger: MonsterHuntDeck["items"][number]
}

interface RoundFeedback {
  correct: boolean
  damage: number
  revealedValue: number
  reason: "guess" | "timeout"
  awaitingGuard?: boolean
  guarded?: boolean
}

function streakMultiplier(streak: number) {
  if (streak >= 9) return 2
  if (streak >= 6) return 1.55
  if (streak >= 3) return 1.25
  return 1
}

function streakTier(streak: number) {
  if (streak >= 9) return 3
  if (streak >= 6) return 2
  if (streak >= 3) return 1
  return 0
}

function pulseHaptics(pattern: number | number[]) {
  if (
    typeof navigator !== "undefined" &&
    typeof navigator.vibrate === "function"
  ) {
    navigator.vibrate(pattern)
  }
}

function buildBattleQueue(decks: MonsterHuntDeck[], length = 40) {
  const usableDecks = shuffleArray(
    decks.filter((deck) => deck.items.length >= 4),
  )
  if (!usableDecks.length) return []

  const itemPools = usableDecks.map((deck) => shuffleArray(deck.items))
  const cursors = usableDecks.map(() => 0)
  const queue: BattleRound[] = []

  for (let roundIndex = 0; roundIndex < length; roundIndex += 1) {
    const deckIndex = roundIndex % usableDecks.length
    const deck = usableDecks[deckIndex]
    const pool = itemPools[deckIndex]
    let cursor = cursors[deckIndex]

    if (cursor + 1 >= pool.length) {
      itemPools[deckIndex] = shuffleArray(deck.items)
      cursor = 0
    }

    let current = itemPools[deckIndex][cursor]
    let challenger = itemPools[deckIndex][cursor + 1]
    let attempts = 0
    while (
      current.value === challenger.value &&
      attempts < itemPools[deckIndex].length
    ) {
      cursor = (cursor + 1) % itemPools[deckIndex].length
      current = itemPools[deckIndex][cursor]
      challenger =
        itemPools[deckIndex][(cursor + 1) % itemPools[deckIndex].length]
      attempts += 1
    }

    cursors[deckIndex] = cursor + 2
    queue.push({
      id: `${deck.id}-${current.id}-${challenger.id}-${roundIndex}`,
      deck,
      current,
      challenger,
    })
  }

  return queue
}

function HealthBar({
  current,
  maximum,
  label,
  tone,
}: {
  current: number
  maximum: number
  label: string
  tone: "player" | "boss"
}) {
  const percentage = Math.max(0, Math.min(100, (current / maximum) * 100))

  return (
    <div className={styles.healthGroup} data-tone={tone}>
      <span className={styles.healthLabel}>
        <span>{label}</span>
        <strong>
          {Math.max(0, current)} / {maximum}
        </strong>
      </span>
      <span
        className={styles.healthTrack}
        role="progressbar"
        aria-label={`${label} health`}
        aria-valuemin={0}
        aria-valuemax={maximum}
        aria-valuenow={Math.max(0, current)}
      >
        <span style={{ width: `${percentage}%` }} />
      </span>
    </div>
  )
}

function ComparisonImage({
  image,
  name,
  priority = false,
}: {
  image: string | null
  name: string
  priority?: boolean
}) {
  if (!image) {
    return (
      <span className={styles.itemFallback} aria-hidden="true">
        {name.slice(0, 2).toUpperCase()}
      </span>
    )
  }

  return (
    <Image
      src={image}
      alt=""
      fill
      sizes="(max-width: 760px) 84vw, 34vw"
      className={styles.itemImage}
      priority={priority}
      fetchPriority={priority ? "high" : "auto"}
      quality={60}
    />
  )
}

export function MonsterHuntGame({ decks }: { decks: MonsterHuntDeck[] }) {
  const [difficulty, setDifficulty] =
    useState<MonsterHuntDifficulty>("hunter")
  const [phase, setPhase] = useState<MonsterHuntPhase>("setup")
  const [battleQueue, setBattleQueue] = useState<BattleRound[]>([])
  const [roundIndex, setRoundIndex] = useState(0)
  const [bossHealth, setBossHealth] = useState<number>(
    DIFFICULTIES.hunter.bossHealth,
  )
  const [playerHealth, setPlayerHealth] = useState(100)
  const [streak, setStreak] = useState(0)
  const [bestStreak, setBestStreak] = useState(0)
  const [secondsLeft, setSecondsLeft] = useState<number>(
    DIFFICULTIES.hunter.seconds,
  )
  const [roundFeedback, setRoundFeedback] =
    useState<RoundFeedback | null>(null)
  const [impact, setImpact] = useState<MonsterImpact>("idle")
  const [impactSignal, setImpactSignal] = useState(0)
  const [defenseActive, setDefenseActive] = useState(false)
  const [defenseSignal, setDefenseSignal] = useState(0)
  const [combatMessage, setCombatMessage] = useState(
    "Choose a difficulty to enter the arena.",
  )
  const resolvingRef = useRef(false)
  const defenseActiveRef = useRef(false)
  const lastUrgentTickRef = useRef(-1)
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const defenseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const difficultyConfig = DIFFICULTIES[difficulty]
  const currentRound = battleQueue[roundIndex]
  const currentMultiplier = streakMultiplier(streak)
  const currentTier = streakTier(streak)

  const outcomeStats = useMemo(
    () => ({
      rounds: Math.min(roundIndex + (phase === "won" ? 1 : 0), battleQueue.length),
      difficulty: difficultyConfig.label,
      bestStreak,
    }),
    [
      battleQueue.length,
      bestStreak,
      difficultyConfig.label,
      phase,
      roundIndex,
    ],
  )

  const preloadUpcomingRounds = useCallback(
    (fromIndex: number) => {
      if (typeof window === "undefined") return
      battleQueue
        .slice(fromIndex, fromIndex + 4)
        .flatMap((round) => [
          round.current.image,
          round.challenger.image,
          ...(round.current.images ?? []),
          ...(round.challenger.images ?? []),
        ])
        .filter((source): source is string => Boolean(source))
        .forEach((source) => {
          const image = new window.Image()
          image.decoding = "async"
          image.src = source
        })
    },
    [battleQueue],
  )

  const clearTransition = useCallback(() => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current)
      transitionTimeoutRef.current = null
    }
  }, [])

  const clearDefense = useCallback(() => {
    if (defenseTimeoutRef.current) {
      clearTimeout(defenseTimeoutRef.current)
      defenseTimeoutRef.current = null
    }
    defenseActiveRef.current = false
    setDefenseActive(false)
  }, [])

  const startBattle = useCallback(() => {
    const queue = buildBattleQueue(decks)
    if (!queue.length) return

    clearTransition()
    clearDefense()
    resolvingRef.current = false
    setBattleQueue(queue)
    setRoundIndex(0)
    setBossHealth(difficultyConfig.bossHealth)
    setPlayerHealth(100)
    setStreak(0)
    setBestStreak(0)
    setSecondsLeft(difficultyConfig.seconds)
    setRoundFeedback(null)
    setImpact("idle")
    setImpactSignal((signal) => signal + 1)
    setCombatMessage(
      `${difficultyConfig.label} difficulty selected. The Data Golem is ready.`,
    )
    lastUrgentTickRef.current = -1
    playSelectSound()
    setPhase("playing")
    trackProductEvent("monster_hunt_started", {
      properties: {
        difficulty,
        boss_health: difficultyConfig.bossHealth,
        player_damage: difficultyConfig.playerDamage,
        monster_damage: difficultyConfig.monsterDamage,
        round_seconds: difficultyConfig.seconds,
        guard_window_ms: difficultyConfig.guardWindowMs,
      },
    })
  }, [
    clearDefense,
    clearTransition,
    decks,
    difficulty,
    difficultyConfig,
  ])

  const finishBattle = useCallback(
    (outcome: "won" | "lost", finalBestStreak: number, completedRounds: number) => {
      clearDefense()
      setPhase(outcome)
      setImpact(outcome === "won" ? "victory" : "defeat")
      setImpactSignal((signal) => signal + 1)
      setCombatMessage(
        outcome === "won"
          ? "The Data Golem is down. The arena is yours."
          : "The Data Golem wins this round. Your next hunt can start now.",
      )
      if (outcome === "won") {
        playVictorySound()
        pulseHaptics([25, 35, 25, 35, 60])
      } else {
        playGameOverSound()
        pulseHaptics([70, 35, 95])
      }
      trackProductEvent("monster_hunt_completed", {
        properties: {
          difficulty,
          outcome,
          rounds: completedRounds,
          best_streak: finalBestStreak,
        },
      })
    },
    [clearDefense, difficulty],
  )

  const advanceRound = useCallback(() => {
    clearDefense()
    setRoundFeedback(null)
    setImpact("idle")
    setRoundIndex((index) => {
      const nextIndex = index + 1
      preloadUpcomingRounds(nextIndex)
      return nextIndex
    })
    setSecondsLeft(difficultyConfig.seconds)
    lastUrgentTickRef.current = -1
    resolvingRef.current = false
  }, [clearDefense, difficultyConfig.seconds, preloadUpcomingRounds])

  const completeBossStrike = useCallback(
    (guarded: boolean) => {
      if (!defenseActiveRef.current || phase !== "playing") return

      clearDefense()
      const damage = guarded
        ? Math.max(
            1,
            Math.round(
              difficultyConfig.monsterDamage *
                difficultyConfig.guardedDamageFactor,
            ),
          )
        : difficultyConfig.monsterDamage
      const nextPlayerHealth = Math.max(0, playerHealth - damage)

      setPlayerHealth(nextPlayerHealth)
      setRoundFeedback((feedback) =>
        feedback
          ? {
              ...feedback,
              damage,
              awaitingGuard: false,
              guarded,
            }
          : feedback,
      )
      setImpact(guarded ? "guard" : "player-hit")
      setImpactSignal((signal) => signal + 1)
      setCombatMessage(
        guarded
          ? `Perfect guard. The counterattack was reduced to ${damage} damage.`
          : `The Data Golem landed its counterattack for ${damage} damage.`,
      )
      trackProductEvent("monster_hunt_guard_resolved", {
        properties: {
          difficulty,
          guarded,
          monster_damage: damage,
          round: roundIndex + 1,
        },
      })
      if (guarded) {
        playSelectSound()
        pulseHaptics([16, 18, 16])
      } else {
        pulseHaptics(55)
      }

      if (nextPlayerHealth <= 0) {
        transitionTimeoutRef.current = setTimeout(
          () => finishBattle("lost", bestStreak, roundIndex + 1),
          1050,
        )
        return
      }

      transitionTimeoutRef.current = setTimeout(advanceRound, 1150)
    },
    [
      advanceRound,
      bestStreak,
      clearDefense,
      difficulty,
      difficultyConfig.guardedDamageFactor,
      difficultyConfig.monsterDamage,
      finishBattle,
      phase,
      playerHealth,
      roundIndex,
    ],
  )

  const guardAttack = useCallback(() => {
    if (!defenseActiveRef.current) return
    completeBossStrike(true)
  }, [completeBossStrike])

  const resolveRound = useCallback(
    (guess: "higher" | "lower" | "timeout") => {
      if (
        phase !== "playing" ||
        resolvingRef.current ||
        !currentRound
      ) {
        return
      }

      resolvingRef.current = true
      clearTransition()

      const actualDirection =
        currentRound.challenger.value > currentRound.current.value
          ? "higher"
          : "lower"
      const correct = guess !== "timeout" && guess === actualDirection

      if (correct) {
        const nextStreak = streak + 1
        const multiplier = streakMultiplier(nextStreak)
        const damage = Math.round(difficultyConfig.playerDamage * multiplier)
        const nextBossHealth = Math.max(0, bossHealth - damage)
        const nextBestStreak = Math.max(bestStreak, nextStreak)

        setStreak(nextStreak)
        setBestStreak(nextBestStreak)
        setBossHealth(nextBossHealth)
        setRoundFeedback({
          correct: true,
          damage,
          revealedValue: currentRound.challenger.value,
          reason: "guess",
        })
        setImpact("boss-hit")
        setImpactSignal((signal) => signal + 1)
        setCombatMessage(
          `Correct. You dealt ${damage} damage with a ${multiplier} times streak boost.`,
        )
        playCorrectSound()
        pulseHaptics(18)
        trackProductEvent("monster_hunt_answered", {
          gameTypeId: currentRound.deck.id,
          properties: {
            difficulty,
            correct: true,
            damage,
            streak: nextStreak,
            multiplier,
            round: roundIndex + 1,
          },
        })

        if (nextBossHealth <= 0) {
          transitionTimeoutRef.current = setTimeout(
            () => finishBattle("won", nextBestStreak, roundIndex + 1),
            900,
          )
          return
        }
      } else {
        setStreak(0)
        setRoundFeedback({
          correct: false,
          damage: 0,
          revealedValue: currentRound.challenger.value,
          reason: guess === "timeout" ? "timeout" : "guess",
          awaitingGuard: true,
        })
        defenseActiveRef.current = true
        setDefenseActive(true)
        setDefenseSignal((signal) => signal + 1)
        setImpact("boss-charge")
        setImpactSignal((signal) => signal + 1)
        setCombatMessage(
          guess === "timeout"
            ? "Time ran out. Guard before the Data Golem strikes."
            : "Wrong direction. Guard before the Data Golem strikes.",
        )
        playWrongSound()
        trackProductEvent("monster_hunt_answered", {
          gameTypeId: currentRound.deck.id,
          properties: {
            difficulty,
            correct: false,
            reason: guess === "timeout" ? "timeout" : "guess",
            lost_streak: streak,
            round: roundIndex + 1,
          },
        })

        defenseTimeoutRef.current = setTimeout(
          () => completeBossStrike(false),
          difficultyConfig.guardWindowMs,
        )
        return
      }

      transitionTimeoutRef.current = setTimeout(advanceRound, 1050)
    },
    [
      advanceRound,
      bestStreak,
      bossHealth,
      clearTransition,
      completeBossStrike,
      currentRound,
      difficulty,
      difficultyConfig.guardWindowMs,
      difficultyConfig.playerDamage,
      finishBattle,
      phase,
      roundIndex,
      streak,
    ],
  )

  const resetToSetup = useCallback(() => {
    clearTransition()
    clearDefense()
    resolvingRef.current = false
    setPhase("setup")
    setBattleQueue([])
    setRoundIndex(0)
    setRoundFeedback(null)
    setBossHealth(difficultyConfig.bossHealth)
    setPlayerHealth(100)
    setStreak(0)
    setSecondsLeft(difficultyConfig.seconds)
    setImpact("idle")
    setImpactSignal((signal) => signal + 1)
    setCombatMessage("Choose a difficulty to enter the arena.")
  }, [
    clearDefense,
    clearTransition,
    difficultyConfig.bossHealth,
    difficultyConfig.seconds,
  ])

  useEffect(
    () => () => {
      clearTransition()
      clearDefense()
    },
    [clearDefense, clearTransition],
  )

  useEffect(() => {
    if (phase !== "playing" || resolvingRef.current || !currentRound) return

    const interval = window.setInterval(() => {
      setSecondsLeft((remaining) => {
        const next = Math.max(0, Number((remaining - 0.1).toFixed(1)))
        const urgentSecond = Math.ceil(next)
        if (
          next > 0 &&
          next <= 3 &&
          urgentSecond !== lastUrgentTickRef.current
        ) {
          lastUrgentTickRef.current = urgentSecond
          playUrgentTickSound()
        }
        if (next <= 0) {
          window.clearInterval(interval)
          window.setTimeout(() => resolveRound("timeout"), 0)
        }
        return next
      })
    }, 100)

    return () => window.clearInterval(interval)
  }, [currentRound, phase, resolveRound])

  useEffect(() => {
    if (phase === "playing" && battleQueue.length) {
      preloadUpcomingRounds(roundIndex)
    }
  }, [battleQueue.length, phase, preloadUpcomingRounds, roundIndex])

  useEffect(() => {
    if (phase !== "playing") return

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return
      }
      if (event.code === "Space" && defenseActiveRef.current) {
        event.preventDefault()
        guardAttack()
        return
      }
      if (event.key.toLowerCase() === "h" || event.key === "ArrowUp") {
        event.preventDefault()
        resolveRound("higher")
      }
      if (event.key.toLowerCase() === "l" || event.key === "ArrowDown") {
        event.preventDefault()
        resolveRound("lower")
      }
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [guardAttack, phase, resolveRound])

  if (!decks.length) {
    return (
      <section className={styles.unavailable}>
        <Sparkles aria-hidden="true" />
        <h1>Monster Hunt is preparing the arena.</h1>
        <p>The comparison decks are being sharpened. Please check back soon.</p>
        <Link href="/play">Browse the game library</Link>
      </section>
    )
  }

  return (
    <section className={styles.gameShell} aria-labelledby="monster-hunt-title">
      <div
        className={styles.arena}
        data-phase={phase}
        data-impact={impact}
        data-tier={currentTier}
      >
        <div className={styles.arenaBackdrop} aria-hidden="true">
          <span />
          <span />
          <span />
        </div>

        {phase === "setup" ? (
          <div className={styles.setupGrid}>
            <div className={styles.setupCopy}>
              <span className={styles.eyebrow}>
                <Swords aria-hidden="true" />
                New 3D game mode
              </span>
              <h1 id="monster-hunt-title">
                Guess right.
                <span>Hit harder.</span>
              </h1>
              <p>
                Face the Data Golem in a Higher or Lower battle. Every correct
                answer lands a hit. Build a streak to multiply your damage,
                then guard the boss&apos;s counterattacks before they land.
              </p>

              <div
                className={styles.difficultyPicker}
                role="radiogroup"
                aria-label="Choose difficulty"
              >
                {difficultyOrder.map((difficultyId) => {
                  const option = DIFFICULTIES[difficultyId]
                  const Icon = option.icon
                  const selected = difficulty === difficultyId
                  return (
                    <button
                      type="button"
                      key={difficultyId}
                      role="radio"
                      aria-checked={selected}
                      className={styles.difficultyOption}
                      data-selected={selected ? "true" : undefined}
                      onClick={() => {
                        setDifficulty(difficultyId)
                        setBossHealth(option.bossHealth)
                        setSecondsLeft(option.seconds)
                      }}
                    >
                      <span className={styles.difficultyIcon}>
                        <Icon aria-hidden="true" />
                      </span>
                      <span>
                        <small>{option.level}</small>
                        <strong>{option.label}</strong>
                        <em>{option.description}</em>
                      </span>
                      <span
                        className={styles.radioIndicator}
                        aria-hidden="true"
                      />
                    </button>
                  )
                })}
              </div>

              <div className={styles.setupStats}>
                <span>
                  <Zap aria-hidden="true" />
                  <strong>{difficultyConfig.playerDamage}</strong>
                  base hit
                </span>
                <span>
                  <Flame aria-hidden="true" />
                  <strong>{difficultyConfig.monsterDamage}</strong>
                  boss hit
                </span>
                <span>
                  <Clock3 aria-hidden="true" />
                  <strong>{difficultyConfig.seconds}s</strong>
                  per guess
                </span>
              </div>

              <button
                type="button"
                className={styles.startButton}
                onClick={startBattle}
              >
                Enter the arena
                <span aria-hidden="true">
                  <Swords />
                </span>
              </button>
            </div>

            <div className={styles.setupVisual}>
              <div className={styles.bossPlate}>
                <span>Boss 01</span>
                <strong>The Data Golem</strong>
                <small>{difficultyConfig.bossHealth} health</small>
              </div>
              <MonsterScene
                bossHealthRatio={1}
                playerHealthRatio={1}
                impact={impact}
                impactSignal={impactSignal}
                phase={phase}
                streakTier={0}
              />
              <span className={styles.sceneCaption}>
                <span />
                Live 3D arena
              </span>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.combatHud}>
              <HealthBar
                current={playerHealth}
                maximum={100}
                label="You"
                tone="player"
              />
              <div className={styles.roundBadge}>
                <span>Round</span>
                <strong>{roundIndex + 1}</strong>
              </div>
              <HealthBar
                current={bossHealth}
                maximum={difficultyConfig.bossHealth}
                label="Data Golem"
                tone="boss"
              />
            </div>

            <div className={styles.combatScene}>
              <div className={styles.battleStatus}>
                <span>
                  <Gauge aria-hidden="true" />
                  {difficultyConfig.label}
                </span>
                <span data-powered={currentTier > 0 ? "true" : undefined}>
                  <Flame aria-hidden="true" />
                  {streak} streak
                </span>
                <strong>
                  {currentMultiplier}x
                  <small> damage</small>
                </strong>
              </div>
              <MonsterScene
                bossHealthRatio={
                  bossHealth / difficultyConfig.bossHealth
                }
                playerHealthRatio={playerHealth / 100}
                impact={impact}
                impactSignal={impactSignal}
                phase={phase}
                streakTier={currentTier}
              />
              <span
                className={styles.bossPhase}
                data-enraged={
                  bossHealth / difficultyConfig.bossHealth <= 0.3
                    ? "critical"
                    : bossHealth / difficultyConfig.bossHealth <= 0.6
                      ? "active"
                      : undefined
                }
              >
                <span aria-hidden="true" />
                {bossHealth / difficultyConfig.bossHealth <= 0.3
                  ? "Core exposed"
                  : bossHealth / difficultyConfig.bossHealth <= 0.6
                    ? "Golem overclocked"
                    : "Combat systems online"}
              </span>
              {defenseActive ? (
                <div
                  className={styles.guardPrompt}
                  key={defenseSignal}
                  role="alert"
                >
                  <span className={styles.guardPromptIcon}>
                    <Shield aria-hidden="true" />
                  </span>
                  <span className={styles.guardPromptCopy}>
                    <small>Incoming counterattack</small>
                    <strong>Guard now</strong>
                    <em>
                      Reduce {difficultyConfig.monsterDamage} damage before
                      impact.
                    </em>
                  </span>
                  <button type="button" onClick={guardAttack}>
                    <Shield aria-hidden="true" />
                    Guard
                    <kbd>Space</kbd>
                    <span
                      aria-hidden="true"
                      style={{
                        animationDuration: `${difficultyConfig.guardWindowMs}ms`,
                      }}
                    />
                  </button>
                </div>
              ) : null}
              {roundFeedback ? (
                <span
                  className={styles.damageNumber}
                  data-correct={
                    roundFeedback.guarded
                      ? "guarded"
                      : roundFeedback.correct
                        ? "true"
                        : "false"
                  }
                  aria-hidden="true"
                >
                  {roundFeedback.awaitingGuard
                    ? "Brace!"
                    : roundFeedback.correct
                      ? `-${roundFeedback.damage}`
                      : roundFeedback.guarded
                        ? "Guarded!"
                        : `-${roundFeedback.damage}`}
                </span>
              ) : null}
            </div>

            {phase === "playing" && currentRound ? (
              <div className={styles.questionDock}>
                <div className={styles.questionHeader}>
                  <span>
                    <strong>{currentRound.deck.categoryName}</strong>
                    {currentRound.deck.name}
                  </span>
                  <span>
                    Which has the higher {currentRound.deck.valueLabel.toLowerCase()}?
                  </span>
                  <span className={styles.timerValue}>
                    <Clock3 aria-hidden="true" />
                    {secondsLeft.toFixed(1)}s
                  </span>
                </div>
                <div className={styles.timerTrack} aria-hidden="true">
                  <span
                    style={{
                      width: `${Math.max(
                        0,
                        (secondsLeft / difficultyConfig.seconds) * 100,
                      )}%`,
                    }}
                  />
                </div>

                <div className={styles.comparisonGrid}>
                  <article className={styles.itemCard}>
                    <div className={styles.itemMedia}>
                      <ComparisonImage
                        image={currentRound.current.image}
                        name={currentRound.current.name}
                        priority
                      />
                      <span className={styles.itemShade} aria-hidden="true" />
                    </div>
                    <div className={styles.itemCopy}>
                      <span>Known value</span>
                      <h2>{currentRound.current.name}</h2>
                      <strong>
                        {formatValue(
                          currentRound.current.value,
                          currentRound.deck.valueUnit ?? undefined,
                          currentRound.deck.valuePrefix,
                        )}
                      </strong>
                      <small>{currentRound.deck.valueLabel}</small>
                    </div>
                  </article>

                  <span className={styles.versusDisc} aria-hidden="true">
                    VS
                  </span>

                  <article className={styles.itemCard}>
                    <div className={styles.itemMedia}>
                      <ComparisonImage
                        image={currentRound.challenger.image}
                        name={currentRound.challenger.name}
                        priority
                      />
                      <span className={styles.itemShade} aria-hidden="true" />
                    </div>
                    <div className={styles.itemCopy}>
                      <span>
                        {roundFeedback ? "Revealed value" : "Your target"}
                      </span>
                      <h2>{currentRound.challenger.name}</h2>
                      {roundFeedback ? (
                        <>
                          <strong>
                            {formatValue(
                              roundFeedback.revealedValue,
                              currentRound.deck.valueUnit ?? undefined,
                              currentRound.deck.valuePrefix,
                            )}
                          </strong>
                          <small>
                            {roundFeedback.correct
                              ? "Direct hit"
                              : roundFeedback.awaitingGuard
                                ? "Counterattack incoming"
                                : roundFeedback.guarded
                                  ? `${roundFeedback.damage} damage after guard`
                              : roundFeedback.reason === "timeout"
                                ? "Time expired"
                                : "The boss hit back"}
                          </small>
                        </>
                      ) : (
                        <div className={styles.guessButtons}>
                          <button
                            type="button"
                            onClick={() => resolveRound("higher")}
                            aria-label={`Guess ${currentRound.challenger.name} is higher`}
                          >
                            <ArrowUp aria-hidden="true" />
                            Higher
                            <kbd>H</kbd>
                          </button>
                          <button
                            type="button"
                            onClick={() => resolveRound("lower")}
                            aria-label={`Guess ${currentRound.challenger.name} is lower`}
                          >
                            <ArrowDown aria-hidden="true" />
                            Lower
                            <kbd>L</kbd>
                          </button>
                        </div>
                      )}
                    </div>
                  </article>
                </div>
              </div>
            ) : null}

            {phase === "won" || phase === "lost" ? (
              <div className={styles.resultPanel} role="dialog" aria-modal="true">
                <span className={styles.resultIcon} data-result={phase}>
                  {phase === "won" ? (
                    <Trophy aria-hidden="true" />
                  ) : (
                    <Heart aria-hidden="true" />
                  )}
                </span>
                <span className={styles.eyebrow}>
                  {phase === "won" ? "Boss defeated" : "Hunt ended"}
                </span>
                <h2>
                  {phase === "won"
                    ? "The arena is yours."
                    : "The Golem hit harder."}
                </h2>
                <p>
                  {phase === "won"
                    ? "Your streak turned every answer into a heavier strike."
                    : "Change difficulty or rebuild your damage boost and try again."}
                </p>
                <div className={styles.resultStats}>
                  <span>
                    <small>Difficulty</small>
                    <strong>{outcomeStats.difficulty}</strong>
                  </span>
                  <span>
                    <small>Rounds</small>
                    <strong>{outcomeStats.rounds}</strong>
                  </span>
                  <span>
                    <small>Best streak</small>
                    <strong>{outcomeStats.bestStreak}</strong>
                  </span>
                </div>
                <div className={styles.resultActions}>
                  <button type="button" onClick={startBattle}>
                    Fight again
                    <RotateCcw aria-hidden="true" />
                  </button>
                  <button type="button" onClick={resetToSetup}>
                    Change difficulty
                    <Gauge aria-hidden="true" />
                  </button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>

      <p className="sr-only" aria-live="assertive" aria-atomic="true">
        {combatMessage}
      </p>
    </section>
  )
}
